import React, { useState } from 'react';

import { contactApis } from '@apis/next/contactApis';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogContent,
  ActionableDialogContentDivider,
  ActionableDialogTitle,
} from '@components/Dialog';
import { FormTitle } from '@components/Forms/FormTitle';
import { QueryContainer } from '@components/QueryContainer';
import { SpinnerModal } from '@components/SpinnerModal';
import { ContactDisplay } from '@components/utilities/BookingInquiry/ContactDisplay';
import { ContactFilter } from '@components/utilities/BookingInquiry/ContactFilter';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import LinkIcon from '@material-ui/icons/Link';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import { type ContactType, Contact, ContactSearchQuery } from '@models/next/Contact';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { Link } from 'react-router-dom';

const strings = createStrings({
  ja: {
    title: (type: ContactType) => `よく使われる${bookingInquiryStrings[type]}`,
    selectContactPrompt: (type: ContactType) => `${bookingInquiryStrings[type]}を選択してください`,
    addContactPrompt: `ここで連絡先を追加していただくと、予約する時にお便利に選ぶことができます。`,
    confirmDelete: '連絡先との連携を解除しますか？',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  contacts: {
    ...common.flexColumn,
    columnGap: 20,
  },
  contact: {
    ...common.flexInline,
    ...common.itemsStart,
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0px 8px 15px -9px rgba(0, 0, 0, 0.15)',
    padding: '24px 30px',
    paddingRight: 24,
    marginBottom: 12,
  },
  linkButton: {
    ...common.flexInline,
    ...common.textPrimary,
    ...common.itemsCenter,
    columnGap: '0.25rem',
    position: 'relative',
    top: 3,
    cursor: 'pointer',
  },
  linkOffButton: {
    ...common.textGray700,
    marginTop: -2,
  },
  dialog: {
    maxWidth: '960px !important',
  },
  addContactPrompt: {
    ...common.middleTextImportant,
    ...common.textGray700,
    ...common.baseButton,
    padding: '11px 8px',
    backgroundColor: theme.palette.base[200],
  },
}));

interface RelatedContactInputProps {
  open: boolean;
  onClose: () => void;
  onSave: (ids: number[]) => Promise<void>;
  contactType: ContactType;
  relatedContacts: Contact[];
}

const RelatedContactInput: React.FC<RelatedContactInputProps> = (props) => {
  const { open, onClose, onSave, contactType, relatedContacts } = props;

  const [query, setQuery] = useState<ContactSearchQuery>({
    company_name: '',
    principal_name: '',
  });

  const allContacts = contactApis.useGet('/contact', {
    queries: {
      ...query,
      contact_type: contactType,
    },
  });

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [selectedIds, setSelectedIds] = useState(relatedContacts.map(({ id }) => id));

  return (
    <ActionableDialog
      open={open}
      onClose={onClose}
      classes={{
        paper: classes.dialog,
      }}
      muiDialogProps={{
        disablePortal: false,
      }}
    >
      <ActionableDialogTitle>{strings.selectContactPrompt(contactType)}</ActionableDialogTitle>
      <ActionableDialogContent>
        <QueryContainer style={{ height: 478 }} queries={[allContacts]}>
          {() => (
            <ContactFilter
              contacts={allContacts.data!}
              title={bookingInquiryStrings[contactType]}
              selectedIds={selectedIds}
              onChangeSelectedIds={setSelectedIds}
              onAddContact={() =>
                window.open(
                  contactType === 'shipper'
                    ? url('/utilities/Contact/Shippers/AddShipper')
                    : url('/utilities/Contact/Consignees/AddConsignee'),
                  '_blank',
                )
              }
              onEditContact={() =>
                window.open(
                  contactType === 'shipper'
                    ? url('/utilities/Contact/Shippers/Shippers')
                    : url('/utilities/Contact/Consignees/Consignees'),
                  '_blank',
                )
              }
              onRefresh={() => {
                allContacts.remove();
                allContacts.refetch();
              }}
              query={query}
              onChangeQuery={setQuery}
            />
          )}
        </QueryContainer>
      </ActionableDialogContent>

      <ActionableDialogContentDivider />

      <div style={{ padding: '1rem 24px' }}>
        <Button
          color="primary"
          text={formStrings.ok}
          size="small"
          data-testid="close-contact-filter"
          onClick={async () => {
            await onSave(selectedIds);
            onClose();
          }}
          style={{ width: '100%' }}
        />
      </div>
    </ActionableDialog>
  );
};

interface RelatedContactProps {
  contact: Contact;
  onDelete: () => void;
}

const RelatedContact: React.FC<RelatedContactProps> = (props) => {
  const { contact, onDelete } = props;

  const classes = useStyles();

  const pathname =
    contact.contact_type === 'shipper'
      ? url(`/utilities/Contact/Shippers/ShipperDetails`)
      : url(`/utilities/Contact/Consignees/ConsigneeDetails`);

  const { renderDialog, confirm } = useConfirmDialog();

  return (
    <div className={classes.contact}>
      {renderDialog()}

      <Link to={`${pathname}?id=${contact.id}`} style={{ display: 'contents' }}>
        <ContactDisplay isRealUser={!!contact.user} contact={contact} />
      </Link>
      <button
        className={classes.linkOffButton}
        onClick={async () => {
          if ((await confirm({ message: strings.confirmDelete })) === 'confirmed') {
            onDelete();
          }
        }}
      >
        <LinkOffIcon />
      </button>
    </div>
  );
};

export interface RelatedContactArrayProps {
  id: number;
  contactType: ContactType;
}

export const RelatedContactArray: React.FC<RelatedContactArrayProps> = (props) => {
  const { id, contactType } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [dialogOpen, setDialogOpen] = useState(false);

  const relatedContactsQuery = contactApis.useGet('/contact', {
    queries: {
      related_to: id,
    },
  });

  const patchContact = contactApis.usePatch('/contact/:id', {
    params: {
      id,
    },
  });

  const title = strings.title(contactType);

  return (
    <div className={classes.flexColumn}>
      <FormTitle
        title={title}
        actions={
          <button className={classes.linkButton} onClick={() => setDialogOpen(true)}>
            <LinkIcon />
            {formStrings.add}
          </button>
        }
      />

      <SpinnerModal open={patchContact.isLoading} />

      <QueryContainer queries={[relatedContactsQuery]} className={classes.contacts}>
        {() => (
          <>
            <RelatedContactInput
              open={dialogOpen}
              key={String(dialogOpen)}
              onClose={() => setDialogOpen(false)}
              onSave={async (ids) => {
                await patchContact.mutateAsync({
                  related_contacts: ids,
                });
                await relatedContactsQuery.refetch();
              }}
              contactType={contactType}
              relatedContacts={relatedContactsQuery.data!}
            />

            {relatedContactsQuery.data?.length === 0 && (
              <div className={classes.addContactPrompt} onClick={() => setDialogOpen(true)}>
                {strings.addContactPrompt}
              </div>
            )}

            {relatedContactsQuery.data?.map((contact) => (
              <RelatedContact
                key={contact.id}
                contact={contact}
                onDelete={async () => {
                  await patchContact.mutateAsync({
                    related_contacts: relatedContactsQuery.data
                      ?.map(({ id }) => id)
                      .filter((id) => id !== contact.id),
                  });
                  await relatedContactsQuery.refetch();
                }}
              />
            ))}
          </>
        )}
      </QueryContainer>
    </div>
  );
};
